import { memo, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { BadgeCheck, Eye } from 'lucide-react'
import { Card } from '@/components/play'
import { Seats } from '@/components/watch/Audience'
import { HostAvatar } from '@/components/watch/HostAvatar'
import { personToneKey } from '@/lib/profile'
import { formatAudience, nameOrHandle, profileAvatarUrl } from '@/lib/watch/rules'
import type { OnlinePerson } from '@/lib/watch/useOnline'
import type { ExploreAudience, ExploreHost, ExploreHostAudience } from '@/types/explore'

/** The hosts an event's card shows; the rest are "+N", to the event's page and its switcher. */
export const HOSTS_SHOWN = 3
/** The seats one row draws at most, as many as the API names; it shows as many whole ones as fit. */
const ROW_SEATS = 12

const seat = (key: string, handle: string | null = null, avatarUrl: string | null = null): OnlinePerson => ({
  key,
  name: null,
  handle,
  avatarUrl,
  toneKey: handle ? personToneKey({ handle }) : null,
  you: false,
  host: false,
})

/** The people the API named, then a neutral disc for each of the others while the row has room. */
function seatsOf({ count, seats }: ExploreHostAudience): OnlinePerson[] {
  const people = seats.map(({ handle, avatar_url }) => seat(handle, handle, profileAvatarUrl(avatar_url)))
  const guests = Array.from({ length: Math.min(count, ROW_SEATS) - people.length }, (_, index) => seat(`guest-${index}`))
  return [...people, ...guests]
}

/** Who to show first: hosts on stage, then the biggest audience, then the API's order. */
function byInterest(hosts: readonly ExploreHost[], audience: ExploreAudience) {
  const watching = ({ stage_id }: ExploreHost) => audience[stage_id]?.count ?? 0
  return [...hosts].sort((a, b) => Number(b.live) - Number(a.live) || watching(b) - watching(a))
}

/**
 * One host of an event, as one link to their page. The watching row is the watch screen's, the eye and count, then
 * seats side by side, and appears only with someone there: an empty room says nothing, and neither does a socket that
 * hasn't answered. The ring is the stage's seats, never the event's schedule. Words only in the accessible name.
 */
const ExploreHostCard = memo(function ExploreHostCard({ host, audience }: { host: ExploreHost; audience?: ExploreHostAudience }) {
  const name = nameOrHandle(host.host.name, host.host.handle, 'Host')
  const seats = useMemo(() => (audience ? seatsOf(audience) : null), [audience])
  const count = audience?.count ?? 0
  const label = [name, host.host.verified && 'verified', host.live && 'live', count > 0 && `${formatAudience(count)} watching`].filter(Boolean).join(', ')
  return (
    <Card as="li" className="explore-host" action={{ to: host.path, label }}>
      <div className="explore-host__who" aria-hidden="true">
        <HostAvatar host={{ ...host.host, name, avatar_url: profileAvatarUrl(host.host.avatar_url) }} size="compact" live={host.live} />
        <span className="explore-host__name">{name}</span>
        {host.host.verified && <BadgeCheck className="explore-card__verified" />}
      </div>
      {seats && (
        <div className="watch-audience explore-host__audience" aria-hidden="true">
          <span className="explore-host__count">
            <Eye />
            {formatAudience(count)}
          </span>
          <div className="explore-host__row">
            <Seats people={seats} className="watch-audience__seats" />
          </div>
        </div>
      )}
    </Card>
  )
})

/** An event's hosts as cards under it, the most watched first. */
export function ExploreHosts({ hosts, audience, eventPath }: { hosts: readonly ExploreHost[]; audience: ExploreAudience; eventPath: string }) {
  const shown = byInterest(hosts, audience).slice(0, HOSTS_SHOWN)
  const more = hosts.length - shown.length
  return (
    // role="list" because VoiceOver drops list semantics from an unstyled list.
    <ul className="explore-hosts" role="list" aria-label="Hosts">
      {shown.map((host) => <ExploreHostCard key={host.stage_id} host={host} audience={audience[host.stage_id]} />)}
      {more > 0 && (
        <li>
          <Link className="explore-hosts__more" to={eventPath} aria-label={`${more} more hosts`}>+{more}</Link>
        </li>
      )}
    </ul>
  )
}
